import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import ErrorIcon from '@mui/icons-material/Error';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SettingsIcon from '@mui/icons-material/Settings';
import FlagIcon from '@mui/icons-material/Flag';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BuildIcon from '@mui/icons-material/Build';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '@mui/icons-material/Star';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import './AdminBar.css';

const AdminBar = ({ isDarkMode, isAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get('tab');
  const [expanded, setExpanded] = useState(false);

  if (!isAdmin) {
    return null;
  }

  const isActive = (item) => {
    if (item.tabIndex === null) {
      // Admin Panel is active when on /admin with no tab param
      return location.pathname === '/admin' && !currentTab;
    }
    // Other items are active when their tab matches
    return location.pathname === '/admin' && currentTab === String(item.tabIndex);
  };

  const primaryItems = [
    {
      icon: <AdminPanelSettingsIcon fontSize="small" />,
      label: 'Admin Panel',
      path: '/admin',
      tabIndex: null
    },
    {
      icon: <PeopleIcon fontSize="small" />,
      label: 'Users',
      path: '/admin?tab=0',
      tabIndex: 0
    },
    {
      icon: <ErrorIcon fontSize="small" />,
      label: 'Errors',
      path: '/admin?tab=2',
      tabIndex: 2
    }
  ];

  const expandedItems = [
    {
      icon: <HistoryIcon fontSize="small" />,
      label: 'Users History',
      path: '/admin?tab=1',
      tabIndex: 1,
      category: 'User Management'
    },
    {
      icon: <FeedbackIcon fontSize="small" />,
      label: 'Feedback',
      path: '/admin?tab=10',
      tabIndex: 10,
      category: 'User Management'
    },
    {
      icon: <SettingsIcon fontSize="small" />,
      label: 'Key-Value',
      path: '/admin?tab=3',
      tabIndex: 3,
      category: 'System'
    },
    {
      icon: <FlagIcon fontSize="small" />,
      label: 'Feature Flags',
      path: '/admin?tab=4',
      tabIndex: 4,
      category: 'System'
    },
    {
      icon: <PsychologyIcon fontSize="small" />,
      label: 'Models',
      path: '/admin?tab=5',
      tabIndex: 5,
      category: 'System'
    },
    {
      icon: <BuildIcon fontSize="small" />,
      label: 'ConceptGate',
      path: '/admin?tab=6',
      tabIndex: 6,
      category: 'System'
    },
    {
      icon: <BuildIcon fontSize="small" />,
      label: 'Services',
      path: '/admin?tab=7',
      tabIndex: 7,
      category: 'Services'
    },
    {
      icon: <CalendarTodayIcon fontSize="small" />,
      label: 'Calendar',
      path: '/admin?tab=8',
      tabIndex: 8,
      category: 'Services'
    },
    {
      icon: <StarIcon fontSize="small" />,
      label: 'Alpha Access',
      path: '/admin?tab=9',
      tabIndex: 9,
      category: 'Services'
    },
    {
      icon: <CreditCardIcon fontSize="small" />,
      label: 'Subscriptions',
      path: '/admin?tab=11',
      tabIndex: 11,
      category: 'Services'
    }
  ];

  // Group expanded items by category
  const groupedExpanded = expandedItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div className={`admin-bar ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="admin-bar-content">
        {primaryItems.map((item) => (
          <button
            key={item.label}
            className={`admin-bar-item ${isActive(item) ? 'active' : ''}`}
            onClick={() => navigate(item.path)}
            title={item.label}
          >
            {item.icon}
            <span className="admin-bar-item-label">{item.label}</span>
          </button>
        ))}
        
        <button
          className="admin-bar-item admin-bar-expand-toggle"
          onClick={() => setExpanded(!expanded)}
          title={expanded ? 'Show less' : 'Show more'}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          <span className="admin-bar-item-label">{expanded ? 'Less' : 'More'}</span>
        </button>
      </div>

      {expanded && (
        <div className={`admin-bar-expanded ${isDarkMode ? 'dark-mode' : ''}`}>
          {Object.entries(groupedExpanded).map(([category, items]) => (
            <div key={category} className="admin-bar-category">
              <div className="admin-bar-category-header">{category}</div>
              <div className="admin-bar-category-items">
                {items.map((item) => (
                  <button
                    key={item.label}
                    className={`admin-bar-item ${isActive(item) ? 'active' : ''}`}
                    onClick={() => {
                      navigate(item.path);
                      setExpanded(false);
                    }}
                    title={item.label}
                  >
                    {item.icon}
                    <span className="admin-bar-item-label">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBar;

